<template>
  <div @click.stop.shift="editRessource()">
    <div v-if="!hideLabel" class="edit-field-label">{{ label }}</div>
    <div v-if="items.length >= 5 && !disabled" class="select-field">
      <v-combobox v-if="!disabled"
                  ref="COMBO_BOX"
                  v-model=saisieHolder
                  :class="getEditFieldClass"
                  :error=showError
                  :error-messages="showError ? errorText : ''"
                  :filter="removeAccents"
                  :items=orderedItems
                  :outlined="editFieldInfo.theme === EditFieldTheme.OUTLINED"
                  :readonly=disabled
                  :ripple="false"
                  :placeholder="fixedPlaceholder"
                  :persistent-placeholder="true"
                  :single-line="hideLabel || editFieldInfo.theme === EditFieldTheme.OUTLINED"
                  @update:search="onSearch"
                  @update:modelValue="onSelect"
                  @blur="onBlur()"
                  @click="onFocus()"
                  @focus="onFocus()">
        <template #append>
          <v-icon v-if="canEdit" @click="onEdit">fas fa-pencil</v-icon>
          <v-icon v-if="editFieldInfo.ressource.helpHtmlId > 0" class="ms-2" @click="toggleHelpDialog">
            fas
            fa-circle-info
          </v-icon>
        </template>
      </v-combobox>
    </div>

    <div v-else-if="items.length < 5 && !disabled" class="select-field">
      <v-select ref="COMBO_BOX"
                v-model=saisieId
                :class="getEditFieldClass"
                :error=showError
                :error-messages="showError ? errorText : ''"
                :filter="removeAccents"
                :items=itemTexts
                :outlined="editFieldInfo.theme === EditFieldTheme.OUTLINED"
                :readonly=disabled
                :ripple="false"
                :placeholder="fixedPlaceholder"
                :persistent-placeholder="true"
                :single-line="hideLabel || editFieldInfo.theme === EditFieldTheme.OUTLINED"
                @blur="onBlur()"
                @click="onFocus()">
        <template #append>
          <v-icon v-if="canEdit" class="help-icon pt-3" @click="onEdit">fas fa-pencil</v-icon>
          <v-icon v-if="editFieldInfo.ressource.helpHtmlId > 0" class="ms-2 pt-3 help-icon" @click="toggleHelpDialog">
            fas fa-circle-info
          </v-icon>
        </template>
      </v-select>
    </div>

    <div v-if="disabled" class="select-field">
      <v-text-field v-model=saisie.title
                    :disabled=disabled
                    :error=showError
                    :label="saisie.title == '' ? labelOrError : ''"
                    :outlined="editFieldInfo.theme === EditFieldTheme.OUTLINED"
                    :ripple="false"
                    :single-line="hideLabel || editFieldInfo.theme === EditFieldTheme.OUTLINED">
        <template #append>
          <v-icon v-if="editFieldInfo.ressource.helpHtmlId > 0" class="ms-2 help-icon" @click="toggleHelpDialog">
            fas
            fa-circle-info
          </v-icon>
        </template>
      </v-text-field>
    </div>

    <help-dialog v-if="showHelpDialog && editFieldInfo.ressource.helpHtmlId > 0"
                 :open="showHelpDialog"
                 :content="helpHtml"
                 @close="toggleHelpDialog">
    </help-dialog>
  </div>
</template>

<script lang="ts">
  import Item from '@/framework/base/classes/Item';
  import { EditFieldTheme, FieldError, FieldInfo } from '@/framework/base/components/FieldInfo';
  import latinize from 'latinize';
  import { type EditRessource } from '@/framework/base/helpers/Ressources';
  import { Ressources } from '@/framework/base/helpers/Ressources';
  import HelpDialog from '@/framework/base/components/HelpDialog.vue';
  import { RULES_FRENCH_LABEL } from '@/generated/validators/ValidationRules';
  import { defineComponent, type PropType } from "vue";
  import { storeSession } from '@/generated/stores';
  import UserProfile, { EnumProfile } from '@/framework/base/classes/UserProfile';

  export const SELECT_NEW_VALUE = -1;
  export const SELECT_VALUE = -999;
  export const SELECT_TEXT = 'Sélectionner';

  export let cbEditRessource: EditRessource | undefined = undefined;

  export function setEditRessource(editRessource: EditRessource) {
    cbEditRessource = editRessource;
  }

  export default defineComponent({
    components: { HelpDialog },
    props: {
      value: {
        type: String,
        required: true
      },
      disabled: {
        type: Boolean
      },
      items: {
        type: Array as PropType<Item[]>,
        required: true
      },
      canEdit: {
        type: Boolean
      },
      editFieldInfo: {
        type: Object as PropType<FieldInfo>,
        required: true
      },
      required: {
        type: Boolean
      },
      hideLabel: {
        type: Boolean
      },
      bold: {
        type: Boolean
      },
      dataTable: {
        type: Boolean
      },
      selectText: {
        type: String
      },
      showMissing: {
        type: Boolean
      },
      contextId: {
        type: Number
      },
      notOrdered: {
        type: Boolean
      }
    },
    data() {
      return {
        cbEditRessource,
        lastContextId: 0,
        error: false,
        initValue: '',
        init: true,
        label: '',
        labelOrError: '',
        test: '',
        showHelpDialog: false,
        loadedHelpHtml: '',
        placeholder: '',
        fixedPlaceholder: 'Sélectionner',
        errorText: '',
        erreurSaisie: false,
        search: '',
        searchText: '',
        isFocusing: false
      };
    },

    computed: {
      EditFieldTheme() {
        return EditFieldTheme;
      },
      orderedItems(): Item[] {
        if (this.notOrdered == true) {
          return this.items;
        } else {
          const ordered: Item[] = [];
          this.items.filter(a => a.key <= 0).forEach((item) => ordered.push(item));
          this.items.filter(a => a.key > 0).sort((a, b) => a.title.toUpperCase() > b.title.toUpperCase() ? 1 : -1).forEach((item) => ordered.push(item));
          return ordered;
        }
      },
      saisieId: {
        get(): string {
          if (this.saisie.title == this.textSelect && this.$refs.COMBO_BOX) {
            // @ts-expect-error : generated method
            this.$refs.COMBO_BOX.reset();
          }
          return this.saisie.title;
        },
        set(value: string) {
          const item = this.orderedItems.find(a => a.title == value);
          if (item) {
            this.saisie = item
          }
        }
      },
      itemTexts(): string[] {
        const res: string[] = [];
        let found = false;
        this.orderedItems.forEach((item) => {
          if (item.title == this.saisie.title) {
            found = true;
          }
          res.push(item.title)
        });
        if (!found) {
          this.setPlaceholder(this.saisie.title);
        }
        return res;
      },
      saisieHolder: {
        get(): Item | any {
          if (this.saisie.key > 0) {
            return this.saisie;
          } else {
            if (this.searchText != '') {
              return this.searchText;
            }
            return undefined;
          }
        },
        set(item: Item | any) {
          if (typeof item === 'string') {
            this.searchText = item;
          }
          if (item || item == '') {
            this.saisie = item;
          }
        }
      },
      saisie: {
        get(): Item {
          if (this.value != null) {
            let value = this.value.toString()
            if (this.init && value == '' && this.initValue != '') {
              value = this.initValue;
              this.setInitValue('');
            }
            const item = this.getItem(value);
            if (item.key >= 0) {
              this.checkError(item, true);
            }
            if (this.items.length > 0 && this.showMissing) {
              this.checkError(item, true);
            }
            if (this.contextId != this.lastContextId && this.contextId) {
              this.checkError(item, false);
              this.setContextId(this.contextId);
            }
            if (this.showMissing) {
              this.checkError(item, true);
            }
            return item;
          } else {
            return new Item(0, 'erreur');
          }
        },
        set(item: Item) {
          const error = this.error;
          if (item && item.key != null) {
            this.init = false;
            const updated = this.value != item.title;
            if (typeof (item.key) == "number" && item.key != 0) {
              this.error = this.checkError(item, true);
            }
            if (updated) {
              this.$emit('updated', item);
              if (this.error) {
                this.$emit('error', new FieldError(0, '', this.error));
              }
            }
          } else {
            if (!item || !item.key) {
              const saisie = item ? item.toString() : '';
              const match = this.orderedItems.find(a => a.title.toUpperCase() == saisie.toUpperCase());
              if (match) {
                item = match;
              } else {
                item = new Item(SELECT_VALUE, this.textSelect);
              }
              this.saisie = item;
            }
            this.error = this.checkError(item, true);
          }
          if (!this.error) {
            this.labelOrError = this.label;
          }
          if (error != this.error) {
            this.$emit('error', new FieldError(0, '', this.error));
          }
        }
      },
      showError(): boolean {
        return this.error;
      },
      helpHtml(): string {
        if (this.showHelpDialog && this.loadedHelpHtml == '' && this.editFieldInfo.ressource.helpHtmlId > 0) {
          this.getHelpResources(this.editFieldInfo.ressource.helpHtmlId);
        }
        return this.loadedHelpHtml;
      },
      textSelect(): string {
        let res = SELECT_TEXT;
        if (this.selectText) {
          res = this.selectText;
        }
        return res;
      },
      getEditFieldClass(): string {
        let res = 'edit-field';
        if (this.bold) {
          res = 'edit-field-bold';
        }
        if (this.dataTable) {
          res += ' data-table-custom';
        }
        return res;
      }
    },
    created(): void {
      this.error = false;
      this.label = this.editFieldInfo.ressource.label;
      this.labelOrError = this.label;
    },
    methods: {
      getHelpResources(value: number) {
        Ressources.getHelp(value).then((res) => {
          this.loadedHelpHtml = res.html;
        });
      },
      setInitValue(value: string) {
        this.initValue = value;
      },
      setContextId(value: number) {
        this.lastContextId = value;
      },
      setPlaceholder(value: string) {
        this.placeholder = value;
      },
      onBlur() {
        if (this.search && this.search != '' && this.search != this.textSelect) {
          const match = this.orderedItems.find(a => a.title.toUpperCase() == this.search.toUpperCase());
          if (!match) {
            this.erreurSaisie = true;
            this.checkError(this.saisie, true);
          }
        }
      },
      onFocus() {
        if (this.isFocusing) return;
        this.isFocusing = true;

        setTimeout(() => {
          const comboBox = this.$refs.COMBO_BOX;
          const inputElement = comboBox?.$el?.querySelector('input');
          if (inputElement) {
            inputElement.select();
          }
          this.$emit('focus');
          this.isFocusing = false;
        }, 50);
      },
      missing(): boolean {
        return this.required && this.value == '';
      },
      touch(): boolean {
        const error = this.error;
        this.error = this.checkError(this.getItem(this.value ? this.value.toString() : ''), true);
        if (error != this.error) {
          this.$emit('error', new FieldError(0, '', this.error));
        }
        return this.error;
      },
      onEdit() {
        this.$emit('onEdit');
      },
      setFocus(): void {
        // @ts-expect-error : generated method
        this.$refs.COMBO_BOX.focus();
      },
      getItem(value: string): Item {
        let item = this.orderedItems.find(i => i.title == value);
        if (!item) {
          item = this.orderedItems.find(i => i.key == 0);
          if (!item || value == '') {
            item = new Item(-1, this.editFieldInfo?.ressource.placeholder ? this.editFieldInfo.ressource.placeholder : this.textSelect);
          }
        }
        return item;
      },
      removeAccents(item: never, queryText: string, itemText: string): boolean {
        const match = latinize(itemText).toLowerCase().indexOf(latinize(queryText).toLowerCase()) > -1;
        return match;
      },
      onSelect(value: string) {
        this.search = '';
      },
      onSearch(value: string) {
        this.search = value;
        let erreurSaisie = false;
        if (value == '' || value == this.textSelect) {
          erreurSaisie = false;
        } else if (value) {
          const match = this.orderedItems.find(a => a.title.toUpperCase().includes(value.toUpperCase()));
          erreurSaisie = !match;
        }
        if (erreurSaisie != this.erreurSaisie) {
          this.erreurSaisie = erreurSaisie;
          this.checkError(this.saisie, true);
        }
      },
      checkError(item: Item, displayError: boolean): boolean {
        let error = false;
        if (this.erreurSaisie || (this.required && (typeof (item.key) == "number" && item.key <= 0) && displayError)) {
          error = true;
          this.errorText = this.label + ' (' + RULES_FRENCH_LABEL.text.required.error + ')';
        }
        if (!this.error) {
          this.labelOrError = this.label;
        }
        // Envoi du status d'erreur
        if (error != this.error) {
          this.error = error;
          this.$emit('error', new FieldError(0, '', this.error));
        }
        return this.error;
      },
      editRessource() {
        const user = storeSession.connectedUser;
        if (user) {
          const isAdmin = UserProfile.hasProfile(user, EnumProfile.admin);
          if (isAdmin && cbEditRessource) {
            cbEditRessource(this.editFieldInfo.ressource, this.updateRessource);
          }
        }
      },
      updateRessource() {
        this.loadedHelpHtml = '';
      },
      toggleHelpDialog() {
        this.showHelpDialog = !this.showHelpDialog;
      }
    }
  })

</script>
